import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Mic, MicOff, Volume2, Loader2, Send, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Badge } from './ui/badge';
import { useLocale } from '../context/LocaleContext';
import { Input } from './ui/input';

const VoiceCommands = () => {
  const { toast } = useToast();
  const { t, locale } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [processing, setProcessing] = useState(false);
  const [response, setResponse] = useState(null);
  const [recognition, setRecognition] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [iosError, setIosError] = useState(false);
  
  // Use ref to track transcript for the onend callback (fixes closure issue)
  const transcriptRef = useRef('');
  
  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    // Check if browser supports speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event) => {
        const current = event.resultIndex;
        const transcriptText = event.results[current][0].transcript;
        setTranscript(transcriptText);
        transcriptRef.current = transcriptText; // Update ref too
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
        // Use ref value to get the latest transcript (fixes closure issue)
        if (transcriptRef.current.trim()) {
          handleVoiceCommand(transcriptRef.current);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        // Handle iOS-specific errors
        if (event.error === 'service-not-allowed' || event.error === 'not-allowed') {
          setVoiceSupported(false);
          if (isIOS) {
            setIosError(true);
            toast({
              title: 'Voice Not Available on iOS',
              description: 'Please use text input below, or enable Dictation in Settings → General → Keyboard → Enable Dictation',
              variant: 'destructive',
              duration: 8000
            });
          } else {
            toast({
              title: 'Microphone Access Denied',
              description: 'Please allow microphone access in your browser settings to use voice commands.',
              variant: 'destructive'
            });
          }
        } else if (event.error === 'no-speech') {
          toast({
            title: 'No Speech Detected',
            description: 'Please try again and speak clearly.',
            variant: 'destructive'
          });
        } else if (event.error === 'network') {
          toast({
            title: 'Network Error',
            description: 'Please check your internet connection.',
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Voice Recognition Error',
            description: `Error: ${event.error}. Try using text input instead.`,
            variant: 'destructive'
          });
        }
      };

      setRecognition(recognitionInstance);
    } else {
      setVoiceSupported(false);
    }
  }, [isIOS]);

  const startListening = () => {
    if (!voiceSupported) {
      toast({
        title: 'Voice Not Supported',
        description: 'Please use text input below to send commands.',
        variant: 'destructive'
      });
      return;
    }
    
    if (recognition) {
      setTranscript('');
      transcriptRef.current = ''; // Reset ref too
      setResponse(null);
      setIsListening(true);
      try {
        recognition.start();
      } catch (e) {
        console.error('Failed to start recognition:', e);
        setVoiceSupported(false);
        toast({
          title: 'Voice Error',
          description: 'Voice recognition unavailable. Please use text input.',
          variant: 'destructive'
        });
        setIsListening(false);
      }
    } else {
      setVoiceSupported(false);
      toast({
        title: 'Not Supported',
        description: 'Voice commands not supported. Please use text input below.',
        variant: 'destructive'
      });
    }
  };

  const stopListening = () => {
    if (recognition) {
      recognition.stop();
      setIsListening(false);
    }
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim()) {
      setTranscript(textInput);
      handleVoiceCommand(textInput);
      setTextInput('');
    }
  };

  const handleVoiceCommand = async (command) => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assistant/voice-command`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ command })
        }
      );

      if (response.ok) {
        const result = await response.json();
        setResponse(result);
        
        // Speak the response
        if ('speechSynthesis' in window && result.response) {
          const utterance = new SpeechSynthesisUtterance(result.response);
          window.speechSynthesis.speak(utterance);
        }

        toast({
          title: '✅ Command Executed',
          description: result.action || 'Voice command processed'
        });
      }
    } catch (error) {
      console.error('Voice command error:', error);
      toast({
        title: 'Command Failed',
        description: 'Unable to process voice command',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const exampleCommands = [
    'Add task: Review project proposal',
    'Log 8 glasses of water',
    'Show my tasks for today',
    'Create workout session',
    'How many calories today?'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="border-teal-300 hover:bg-teal-50 px-2 sm:px-3"
        >
          <Mic className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">{t('home.voice')}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-teal-600" />
            {t('chat.voiceInput')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* iOS Warning */}
          {iosError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Voice not available on iOS Safari</p>
                <p className="text-xs text-amber-600 mt-1">
                  To enable: Settings → General → Keyboard → Enable Dictation
                  <br />
                  Or use the text input below.
                </p>
              </div>
            </div>
          )}

          {/* Voice Input - Only show if supported */}
          {voiceSupported && !iosError && (
            <div className="flex flex-col items-center gap-4">
              <Button
                size="lg"
                onClick={isListening ? stopListening : startListening}
                disabled={processing}
                className={`h-24 w-24 rounded-full ${
                  isListening
                    ? 'bg-red-600 hover:bg-red-700 animate-pulse'
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                {isListening ? (
                  <MicOff className="h-10 w-10" />
                ) : (
                  <Mic className="h-10 w-10" />
                )}
              </Button>
              
              <p className="text-sm text-gray-600">
                {isListening ? t('cards.chatDesc') : t('home.talkToAllora')}
              </p>
            </div>
          )}
          
          {/* Text Input - Always show as fallback */}
          <form onSubmit={handleTextSubmit} className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              {voiceSupported && !iosError ? 'Or type your command:' : 'Type your command:'}
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="e.g., Add task: Buy groceries"
                disabled={processing}
                className="flex-1"
              />
              <Button 
                type="submit" 
                disabled={processing || !textInput.trim()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>

          {/* Transcript */}
          {transcript && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">{t('common.youSaid')}</p>
              <p className="text-gray-900 font-medium">{transcript}</p>
            </div>
          )}

          {/* Processing */}
          {processing && (
            <div className="text-center py-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" />
              <p className="text-sm text-gray-600 mt-2">{t('common.processingCommand')}</p>
            </div>
          )}

          {/* Response */}
          {response && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{t('common.action')}: {response.action}</Badge>
              </div>
              <p className="text-sm text-gray-900">{response.response}</p>
            </div>
          )}

          {/* Example Commands */}
          {!isListening && !processing && !response && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">{t('common.exampleCommands')}</p>
              <div className="space-y-2">
                {exampleCommands.map((cmd, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700"
                  >
                    "{cmd}"
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceCommands;